import { usePermissions } from "hooks/usePermissions";
import { IconType } from "react-icons";
import TooltipHorizon from "widgets/controls/tooltip";

const CircleButton = ({
  loading = false,
  disabled = false,
  size = "lg",
  variant = "solid",
  type = "button",
  color = "50",
  label,
  onClick,
  extra,
  icon: Icon,
  iconPosition = "left",
  iconColor = "white",
  permissionRole,
  tooltip,
  tooltipPlacement = "top",
}: {
  loading?: boolean;
  disabled?: boolean;
  size?: "xxl" | "lg" | "md" | "sm" | "xs" | "xxs";
  variant?: "solid" | "outline" | "ghost";
  type?: "button" | "submit";
  color?: "primary" | "secondary" | "tertiary" | "white" | string;
  label?: string;
  onClick?: () => void;
  extra?: string;
  icon?: IconType;
  iconColor?: string;
  iconPosition?: "left" | "right";
  permissionRole?: string;
  tooltip?: string | JSX.Element;
  tooltipPlacement?: "left" | "right" | "top" | "bottom";
}) => {
  const { canShow } = usePermissions();

  if (permissionRole && !canShow(permissionRole)) {
    return null;
  }

  let typeClass = "";
  let sizeClass = "";
  let sizeIconClass = "";
  let labelClass = "";
  let opacityClass = disabled || loading ? "opacity-40" : "opacity-100";

  // Named color classes
  const colorClasses = {
    primary: {
      solid: "text-white bg-primary hover:opacity-50",
      outline:
        "border-2 border-primary text-primary bg-transparent hover:opacity-50",
      ghost: "text-primary bg-transparent hover:opacity-50",
      link: "text-primary underline hover:opacity-50",
    },
    secondary: {
      solid: "text-white bg-secondary hover:opacity-50",
      outline:
        "border-2 border-secondary text-secondary bg-transparent hover:opacity-50",
      ghost: "text-secondary bg-transparent hover:opacity-50",
      link: "text-secondary underline hover:opacity-50",
    },
    tertiary: {
      solid: "text-white bg-tertiary hover:opacity-50",
      outline:
        "border-2 border-tertiary text-tertiary bg-transparent hover:opacity-50",
      ghost: "text-tertiary bg-transparent hover:opacity-50",
      link: "text-tertiary underline hover:opacity-50",
    },
    white: {
      solid: "text-primary bg-white hover:opacity-50",
      outline:
        "border-2 border-white text-white bg-transparent hover:opacity-50",
      ghost: "text-white bg-transparent hover:opacity-50",
      link: "text-white underline hover:opacity-50",
    },
  } as const;

  // Type guard for named colors
  function isNamedColorKey(color: string): color is keyof typeof colorClasses {
    return color in colorClasses;
  }

  // Determine the class based on color and variant
  typeClass = isNamedColorKey(color)
    ? colorClasses[color][variant]
    : colorClasses["primary"][variant];

  switch (size) {
    case "xxl":
      sizeClass = "h-14 heading-md";
      sizeIconClass = "h-14 w-14 p-0";
      labelClass = Icon ? (iconPosition === "left" ? "pr-4" : "pl-4") : "px-4";
      break;
    case "lg":
      sizeClass = "h-12 heading-md";
      sizeIconClass = "h-10 w-10 p-0";
      labelClass = Icon ? (iconPosition === "left" ? "pr-4" : "pl-4") : "px-4";
      break;
    case "md":
      sizeClass = "h-10 heading-sm";
      sizeIconClass = "h-8 w-8 p-1";
      labelClass = Icon ? (iconPosition === "left" ? "pr-3" : "pl-3") : "px-3";

      break;
    case "sm":
      sizeClass = "h-8 heading-xs";
      sizeIconClass = "h-6 w-6 p-1";
      labelClass = Icon ? (iconPosition === "left" ? "pr-3" : "pl-3") : "px-3";

      break;
    case "xs":
      sizeClass = "h-6 heading-xs";
      sizeIconClass = "h-4 w-4";
      labelClass = Icon
        ? iconPosition === "left"
          ? "pr-2.5"
          : "pl-2.5"
        : "px-2.5";
      break;
    case "xxs":
      sizeClass = "h-4 heading-xs";
      sizeIconClass = "h-3 w-3";
      labelClass = Icon
        ? iconPosition === "left"
          ? "pr-2.5"
          : "pl-2.5"
        : "px-2.5";
      break;
  }

  const button = (
    <button
      // Apply various classes for styling
      className={`linear flex cursor-pointer flex-row items-center rounded-full ${opacityClass} ${sizeClass} ${typeClass} ${extra}`}
      disabled={loading || disabled}
      onClick={onClick}
      type={type}
    >
      <div className={`flex items-center gap-0 px-1`}>
        {iconPosition === "left" && Icon && (
          <Icon className={`${sizeIconClass} ${iconColor}`} />
        )}

        {label && <span className={`${labelClass}`}>{label}</span>}

        {iconPosition === "right" && Icon && (
          <Icon className={`${sizeIconClass} ${iconColor}`} />
        )}
      </div>
    </button>
  );

  if (tooltip) {
    return (
      <TooltipHorizon
        trigger={button}
        content={typeof tooltip === "string" ? <span>{tooltip}</span> : tooltip}
        placement={tooltipPlacement}
        extra=""
      />
    );
  }

  return button;
};

export default CircleButton;
